import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setUserMessage } from "../../../system/reducers/userMessage/actions";
import moment from "moment";
import { Add, ArrowBack, Delete, Save } from "@mui/icons-material";
import {
	Alert, Autocomplete, Box, Checkbox, CircularProgress, FormControlLabel, Grid, Paper, TextField, Typography
} from "@mui/material";
import Layout from "../../../Components/Layout/Layout";
import CompaniesNavigation from "Navigation/CompaniesNavigation";
import List from "Components/List";
import ListItem from "Components/ListItem";
import ListItemAction from "Components/ListItemAction";
import ListItemActions from "Components/ListItemActions";
import ListItemContent from "Components/ListItemContent";
import Tile from "Components/Tiles/Tile";
import Sidebar from "Components/Sidebar/Sidebar";
import SidebarGroup from "Components/Sidebar/SidebarGroup";
import SidebarButton from "Components/Sidebar/SidebarButton";
import showMessage from "Dialogs/showMessage";
import ask from "Dialogs/ask";
import useCompanies from "../../../system/useCompanies";
import useCompounds from "../../../system/useCompounds";
import {
	TransportAxle, TransportPriceList, TransportPriceListContent, TransportRoutePrice, TransportSurchargeType
} from "../../../system/Domain";
import { axleLabels, getListStatus, surchargeLabels } from "./transportPriceLabels";
import {
	allTypeKeys, buildModel, buildRoutes, EditorModel, OriginModel, ownTypeKey, parseDestinations, placeKey,
	placeLabel
} from "./transportPriceBands";
import TransportPriceOriginSection from "./TransportPriceOriginSection";

type Mode = "create" | "edit" | "view" | "revise";
type Form = Pick<TransportPriceList, "axle" | "validFrom" | "validTo" | "flatFee" | "dieselSurchargeApplies" | "surcharges">;
type Loaded = { list?: TransportPriceList; routes: TransportRoutePrice[] };

const date = (value?: string | null) => (value ? value.substring(0, 10) : "");

const TransportPriceListPage = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const location = useLocation();
	const { carrierId, id } = useParams<{ carrierId: string; id: string }>();
	const [companies] = useCompanies();
	const [compounds] = useCompounds({ includeAmazonCompounds: true });

	const today = moment().format("YYYY-MM-DD");
	const tomorrow = moment().add(1, "day").format("YYYY-MM-DD");
	const isNew = id === "new";
	const basePath = `/companies/transport-prices/${carrierId}`;

	const [areaCodes, setAreaCodes] = useState<string[]>();
	const [loaded, setLoaded] = useState<Loaded>();
	const [mode, setMode] = useState<Mode>("view");
	const [form, setForm] = useState<Form>();
	const [model, setModel] = useState<EditorModel>({});
	const [expanded, setExpanded] = useState<string[]>([]);
	const [originText, setOriginText] = useState("");
	const [isSaving, setIsSaving] = useState(false);

	const compoundOptions = useMemo(() => (compounds ?? []).map(c => ({ id: c.id, name: c.name })), [compounds]);
	const carrier = companies?.find(c => c.id === Number(carrierId));

	const initialize = (data: Loaded, nextMode: Mode, codes: string[], axleOverride?: TransportAxle) => {
		const list = data.list;
		const axle = list?.axle ?? axleOverride ?? "Foreign";
		const nextModel = buildModel(data.routes, codes);

		setLoaded(data);
		setMode(nextMode);
		setModel(nextModel);
		setExpanded(Object.keys(nextModel).slice(0, 1));
		setForm({
			axle,
			validFrom: list ? date(list.validFrom) : tomorrow,
			validTo: list?.validTo ? date(list.validTo) : null,
			flatFee: list?.flatFee ?? null,
			dieselSurchargeApplies: list?.dieselSurchargeApplies ?? false,
			surcharges: list?.surcharges ?? []
		});
	};

	useEffect(() => {
		(async () => {
			const codes = (await axios.get<string[]>("/api/transport-prices/area-codes")).data;
			setAreaCodes(codes);

			if (isNew) {
				const axle = new URLSearchParams(location.search).get("axle") === "Own" ? "Own" : "Foreign";
				initialize({ routes: [] }, "create", codes, axle);
				return;
			}

			const { data } = await axios.get<{ list: TransportPriceList; routes: TransportRoutePrice[] }>(`/api/transport-prices/lists/${id}/full`);
			initialize(data, getListStatus(data.list, today) === "planned" ? "edit" : "view", codes);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	if (!form || !areaCodes || !loaded) {
		return <Typography align="center" component="div"><CircularProgress /></Typography>;
	}

	const list = loaded.list;
	const status = list ? getListStatus(list, today) : "planned";
	const editable = mode !== "view";
	const isOwn = form.axle === "Own";
	const origins = Object.entries(model);

	const updateForm = (changes: Partial<Form>) => setForm(f => ({ ...f!, ...changes }));

	const getSurcharge = (type: TransportSurchargeType) => form.surcharges.find(s => s.type === type)?.amount ?? "";
	const setSurcharge = (type: TransportSurchargeType, amount: string) =>
		updateForm({
			surcharges: [
				...form.surcharges.filter(s => s.type !== type),
				...(amount === "" ? [] : [{ type, amount: Number(amount) }])
			]
		});

	const emptyBands = (): OriginModel["bands"] => (isOwn ? { [ownTypeKey]: [] } : { [allTypeKeys[0]]: [] });

	const addOrigin = () => {
		const { places, errors } = parseDestinations(originText, areaCodes, compoundOptions);
		if (errors.length || places.length !== 1) {
			showMessage({ title: "Fehler", message: errors[0] ?? "Bitte genau einen Ausgangsort (Compound-Name oder PLZ-Gebiet) angeben." });
			return;
		}

		const key = placeKey(places[0]);
		if (!model[key]) {
			setModel(m => ({ [key]: { origin: places[0], bands: emptyBands() }, ...m }));
		}

		setExpanded(e => (e.includes(key) ? e : [key, ...e]));
		setOriginText("");
	};

	const removeOrigin = async (key: string) => {
		const { yes } = await ask(`Alle Preise ab ${placeLabel(model[key].origin)} entfernen?`);
		if (yes) {
			setModel(m => {
				const { [key]: _removed, ...rest } = m;
				return rest;
			});
		}
	};

	const save = async () => {
		const problems: string[] = [];

		if (!form.validFrom) {
			problems.push("Bitte das Datum 'Gültig ab' angeben.");
		} else if (editable && form.validFrom < tomorrow) {
			problems.push("Änderungen gelten frühestens ab morgen.");
		}

		if (form.validTo && form.validTo < form.validFrom) {
			problems.push("'Gültig bis' darf nicht vor 'Gültig ab' liegen.");
		}

		const built = buildRoutes(model, form.axle, areaCodes, compoundOptions);
		problems.push(...built.errors);
		if (!built.routes.length && !built.errors.length) {
			problems.push("Es sind keine Preise erfasst.");
		}

		if (problems.length) {
			await showMessage({
				title: "Bitte korrigieren",
				color: "error",
				maxWidth: "md",
				message: (
					<>
						{problems.slice(0, 30).map((p, i) => <Typography key={i}>{p}</Typography>)}
						{problems.length > 30 && <Typography>… und {problems.length - 30} weitere Hinweise</Typography>}
					</>
				)
			});
			return;
		}

		const body = {
			list: {
				...(list ?? {}),
				carrier: list?.carrier ?? { id: Number(carrierId), name: carrier?.name ?? "" },
				axle: form.axle,
				validFrom: form.validFrom,
				validTo: form.validTo || null,
				flatFee: form.flatFee === null || (form.flatFee as unknown) === "" ? null : Number(form.flatFee),
				dieselSurchargeApplies: form.dieselSurchargeApplies,
				surcharges: form.surcharges.filter(s => form.axle === "Own" || s.type !== "ShortTermPlate")
			},
			routes: built.routes
		} as TransportPriceListContent;

		setIsSaving(true);
		try {
			const { data } = mode === "create"
				? await axios.post<{ list: TransportPriceList; routes: TransportRoutePrice[] }>("/api/transport-prices/lists/full", body)
				: mode === "revise"
					? await axios.post<{ list: TransportPriceList; routes: TransportRoutePrice[] }>(`/api/transport-prices/lists/${id}/revise`, body)
					: await axios.put<{ list: TransportPriceList; routes: TransportRoutePrice[] }>(`/api/transport-prices/lists/${id}/full`, body);

			dispatch(setUserMessage(mode === "revise" ? "Neue Version gespeichert, die bisherige Preisliste endet am Vortag." : "Preisliste gespeichert.", "success"));

			if (data.list.id !== id) {
				history.replace(`${basePath}/lists/${data.list.id}`);
			} else {
				initialize(data, "edit", areaCodes);
			}
		} finally {
			setIsSaving(false);
		}
	};

	const startRevision = () => {
		setMode("revise");
		updateForm({ validFrom: tomorrow, validTo: null });
	};

	const remove = async () => {
		const { yes } = await ask("Soll die Preisliste mit allen Strecken wirklich gelöscht werden?");
		if (yes) {
			await axios.delete(`/api/transport-prices/lists/${id}`);
			history.push(basePath);
		}
	};

	return (
		<Layout
			title={`${axleLabels[form.axle]} – ${carrier?.name ?? "Frachtpreise"}`}
			search="disabled"
			navigation={<CompaniesNavigation />}
			sidebar={(
				<Sidebar>
					{editable && (
						<SidebarGroup>
							<SidebarButton color="primary" startIcon={<Save />} label="Speichern" disabled={isSaving} onClick={save} />
						</SidebarGroup>
					)}
					{mode === "view" && status === "active" && (
						<SidebarGroup>
							<SidebarButton color="primary" startIcon={<Add />} label="Neue Version anlegen" onClick={startRevision} />
						</SidebarGroup>
					)}
					<SidebarGroup>
						<SidebarButton startIcon={<ArrowBack />} label="Zu den Preislisten" onClick={() => history.push(basePath)} />
					</SidebarGroup>
					{mode === "edit" && (
						<SidebarGroup>
							<SidebarButton color="secondary" startIcon={<Delete />} label="Löschen" onClick={remove} />
						</SidebarGroup>
					)}
				</Sidebar>
			)}
		>
			<Paper sx={{ mt: 2, px: 2, pb: 2, pt: 1 }}>
				{mode === "view" && (
					<Box mt={1} mb={1}>
						<Alert severity="info">
							Diese Preisliste ist {status === "active" ? "bereits gültig" : "abgelaufen"} und bleibt zur Nachvollziehbarkeit unverändert und kann nicht gelöscht werden.
							{status === "active" && " Änderungen werden als neue Version mit Gültigkeit ab frühestens morgen angelegt."}
						</Alert>
					</Box>
				)}
				{mode === "revise" && (
					<Box mt={1} mb={1}>
						<Alert severity="info">
							Neue Version: Die bisherige Preisliste endet am Tag vor dem neuen Gültigkeitsbeginn.
						</Alert>
					</Box>
				)}
				<Box>
					<Tile title="Konditionen">
						<Grid container columnSpacing={2} rowSpacing={1}>
							<Grid item xs={12} md={3}>
								<TextField variant="standard" fullWidth type="date" label="Gültig ab" InputLabelProps={{ shrink: true }}
									disabled={!editable} value={form.validFrom}
									inputProps={{ min: editable ? tomorrow : undefined }}
									onChange={e => updateForm({ validFrom: e.target.value })} />
							</Grid>
							<Grid item xs={12} md={3}>
								<TextField variant="standard" fullWidth type="date" label="Gültig bis" InputLabelProps={{ shrink: true }}
									disabled={!editable} value={form.validTo ?? ""}
									helperText={editable ? "Leer = unbefristet" : undefined}
									onChange={e => updateForm({ validTo: e.target.value || null })} />
							</Grid>
							<Grid item xs={12} />
							<Grid item xs={12} md={4}>
								<TextField variant="standard" fullWidth type="number" label={isOwn ? "Pauschale je Fahrzeug (€)" : "Pauschale je Transport (€)"}
									disabled={!editable} value={form.flatFee ?? ""}
									onChange={e => updateForm({ flatFee: e.target.value === "" ? null : Number(e.target.value) })} />
							</Grid>
							<Grid item xs={12} md={4}>
								<TextField variant="standard" fullWidth type="number" label={`${surchargeLabels.MissedTransport} je Fahrzeug (€)`}
									disabled={!editable} value={getSurcharge("MissedTransport")}
									onChange={e => setSurcharge("MissedTransport", e.target.value)} />
							</Grid>
							{isOwn && (
								<Grid item xs={12} md={4}>
									<TextField variant="standard" fullWidth type="number" label={`${surchargeLabels.ShortTermPlate} je Fahrzeug (€)`}
										disabled={!editable} value={getSurcharge("ShortTermPlate")}
										onChange={e => setSurcharge("ShortTermPlate", e.target.value)} />
								</Grid>
							)}
							<Grid item xs={12} md={12}>
								<FormControlLabel
									control={<Checkbox disabled={!editable} checked={form.dieselSurchargeApplies} onChange={e => updateForm({ dieselSurchargeApplies: e.target.checked })} />}
									label="Dieselzuschlag fällt an"
								/>
							</Grid>
						</Grid>
					</Tile>
				</Box>
			</Paper>
			<Box mt={3} sx={{ minWidth: 0 }}>
				{editable && (
					<Box mb={2}>
						<List>
							<ListItem transparent={originText.trim().length < 2}>
								<ListItemActions>
									<ListItemAction icon={<Add />} disabled={originText.trim().length < 2} onClick={addOrigin} />
								</ListItemActions>
								<ListItemContent>
									<Autocomplete
										freeSolo
										fullWidth
										openOnFocus
										options={compoundOptions.map(c => c.name)}
										inputValue={originText}
										onInputChange={(_, value) => setOriginText(value)}
										renderInput={params => (
											<TextField {...params} variant="standard" label="Ausgangsort hinzufügen" placeholder="Compound-Name oder PLZ-Gebiet"
												onKeyDown={e => e.key === "Enter" && originText.trim().length >= 2 && addOrigin()} />
										)}
									/>
								</ListItemContent>
							</ListItem>
						</List>
					</Box>
				)}
				{origins.length === 0 && <Typography color="text.secondary">Noch kein Ausgangsort.</Typography>}
					{origins.map(([key, o]) => (
						<Box key={key} mb={2}>
						<TransportPriceOriginSection
							originKey={key}
							origin={o}
							isOwn={isOwn}
							editable={editable}
							areaCodes={areaCodes}
							compounds={compoundOptions}
							expanded={expanded.includes(key)}
							onToggle={() => setExpanded(e => (e.includes(key) ? e.filter(x => x !== key) : [...e, key]))}
							onChange={next => setModel(m => ({ ...m, [key]: next }))}
							onRemove={() => removeOrigin(key)}
						/>
						</Box>
					))}
			</Box>
		</Layout>
	);
};

export default TransportPriceListPage;
